import api from '@/services/api'
import type { CloserLeadFormData } from '@/types'
import { formatPhone } from '@/utils/formatPhone'
import { normalizePhoneDigits } from '@/utils/formatPhone'
import {
  findEcommercePlatformDuplicate,
  isListedEcommercePlatform,
} from '@/constants/ecommercePlatforms'

export type LeadRegistrationErrors = Record<string, string>

export type EmpresaOcupacao = {
  tipo: string
  responsavel?: string
  label: string
}

/** Deal em pipeline ativo (ganho/perdido/pausado permitem nova prospecção). */
export function isBlockingDealEtapa(etapa?: string | null): boolean {
  const stage = String(etapa || '').toUpperCase()
  if (!stage) return false
  return !['PERDIDO', 'PAUSADO', 'GANHO'].includes(stage)
}

export type EmpresaRegistrationPrefill = Partial<CloserLeadFormData> & {
  source?: 'lost_deal' | 'lead_instance' | 'closer_lead' | 'empresa' | null
}

const ORIGEM_PREFILL_LABELS: Record<string, string> = {
  INBOUND: 'Inbound',
  INDICACAO: 'Indicação',
  EVENTO: 'Evento',
  LINKEDIN: 'LinkedIn',
  REATIVACAO: 'Reativação',
  OUTROS: 'Outro',
}

function normalizeOrigemPrefill(origem?: string | null): string {
  if (!origem?.trim()) return ''
  const key = origem.trim().toUpperCase()
  return ORIGEM_PREFILL_LABELS[key] ?? origem.trim()
}

/** Carrega dados históricos da empresa (deal perdido, lead closer anterior, contatos). */
export async function fetchEmpresaRegistrationPrefill(
  empresaId: string,
): Promise<EmpresaRegistrationPrefill | null> {
  if (!empresaId) return null
  try {
    const { data: payload } = await api.get(`/empresas/${empresaId}/registration-prefill`)
    const row = payload?.data ?? payload
    if (!row || !row.source) return null

    const telefone = row.telefone ? formatPhone(String(row.telefone)) : ''
    return {
      contato: row.contato || '',
      cargo: row.cargo || '',
      telefone,
      email: row.email || '',
      site: row.site || '',
      segmento: row.segmento || '',
      plataforma: row.plataforma || '',
      modelo: row.modelo || 'B2C',
      faturamento: row.faturamento || '',
      trafego: row.trafego || '',
      origem: normalizeOrigemPrefill(row.origem),
      problema: row.problema || '',
      observacoes: row.observacoes || '',
      servicos: Array.isArray(row.servicos) ? row.servicos : [],
      score: typeof row.score === 'number' ? row.score : 50,
      parceiroId: row.parceiroId,
      source: row.source,
    }
  } catch {
    return null
  }
}

/** Valida se empresa já possui deal ou lead ativo (mesma regra do BDR). */
export async function validateEmpresaRegistration(
  empresaId: string | undefined,
): Promise<LeadRegistrationErrors> {
  if (!empresaId) return {}

  try {
    const { data: payload } = await api.get(`/empresas/${empresaId}`)
    const empresa = payload?.data ?? payload
    const deals = (empresa?.deals || []).filter(
      (d: { deletedAt?: string | null }) => !d.deletedAt,
    )
    const leads = (empresa?.leads || []).filter(
      (l: { deletedAt?: string | null }) => !l.deletedAt,
    )
    const leadInstances = (empresa?.leadInstances || []).filter(
      (li: { deletedAt?: string | null }) => !li.deletedAt,
    )

    const blockingDeal = deals.find((d: { etapa?: string }) => isBlockingDealEtapa(d.etapa))
    if (blockingDeal) {
      return { empresa: 'Empresa já está em negociação ativa' }
    }

    const isExistingClient = deals.some(
      (d: { etapa?: string }) => String(d.etapa || '').toUpperCase() === 'GANHO',
    )
    if (isExistingClient) {
      return {}
    }

    const blockingLead = leads.find(
      (l: { status?: string }) => String(l.status || '').toUpperCase() !== 'DESCARTADO',
    )
    if (blockingLead) {
      return { empresa: 'Empresa com lead ativo em prospecção' }
    }

    const blockingInstance = leadInstances.find((li: { status?: string }) => {
      const status = String(li.status || '').toLowerCase()
      return !['descartado', 'perdido', 'cancelado', 'abandonado'].includes(status)
    })
    if (blockingInstance) {
      return { empresa: 'Empresa com lead ativo em prospecção' }
    }
  } catch {
    return { empresa: 'Não foi possível validar a empresa selecionada' }
  }

  return {}
}

/** Valida telefone/e-mail duplicados (mesma regra do cadastro BDR). E-mail é opcional. */
export async function validateContactRegistration(
  telefone: string,
  email?: string,
  excludeLeadId?: string,
  empresaId?: string,
): Promise<LeadRegistrationErrors> {
  const errors: LeadRegistrationErrors = {}
  const phoneDigits = normalizePhoneDigits(telefone)

  if (phoneDigits.length >= 8) {
    try {
      const { data } = await api.get('/closer-leads/check-contact', {
        params: {
          telefone: phoneDigits,
          email: email?.trim() || undefined,
          excludeId: excludeLeadId,
          empresaId: empresaId || undefined,
        },
      })
      const result = data?.data ?? data
      if (result?.telefoneDuplicate) {
        errors.telefone = 'Já existe um lead cadastrado com este telefone'
      }
      if (result?.emailDuplicate) {
        errors.email = 'Já existe um lead cadastrado com este e-mail'
      }
    } catch {
      // fallback silencioso — backend validará no POST
    }
  }

  return errors
}

export function validateRequiredCloserFields(input: {
  empresa?: string
  contato?: string
  telefone?: string
  email?: string
}): LeadRegistrationErrors {
  const errors: LeadRegistrationErrors = {}

  if (!input.empresa?.trim()) errors.empresa = 'Empresa é obrigatória'
  if (!input.contato?.trim()) errors.contato = 'Contato é obrigatório'
  if (!input.telefone?.trim()) errors.telefone = 'Telefone é obrigatório'
  else if (normalizePhoneDigits(input.telefone).length < 8) {
    errors.telefone = 'Telefone inválido'
  }

  const email = input.email?.trim()
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'E-mail inválido'
  }

  return errors
}

/** Bloqueia plataforma que duplica nome já listado (case-insensitive). */
export function validateEcommercePlatformField(plataforma?: string): LeadRegistrationErrors {
  const trimmed = plataforma?.trim()
  if (!trimmed) return {}

  const duplicate = findEcommercePlatformDuplicate(trimmed)
  if (duplicate && duplicate.toLowerCase() === trimmed.toLowerCase()) {
    return {}
  }
  if (duplicate && isListedEcommercePlatform(duplicate)) {
    return { plataforma: `Use "${duplicate}" da lista ou escolha outro nome` }
  }

  return {}
}

/** Rocket: o cadastro precisa dizer se o lead veio de parceiro e, se sim, qual. */
export function validateLeadPartnerChoice(
  input: { temParceiro?: 'sim' | 'nao'; parceiroId?: string | null },
  { requireAnswer }: { requireAnswer: boolean },
): LeadRegistrationErrors {
  const answer = input.temParceiro ?? (input.parceiroId ? 'sim' : undefined)
  if (!answer) return requireAnswer ? { temParceiro: 'Informe se o lead veio de parceiro' } : {}
  if (answer === 'sim' && !input.parceiroId) return { parceiroId: 'Selecione ou cadastre o parceiro' }
  return {}
}
